import { useEffect, useState } from "react";
import { Clock, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatarPrevisaoDeVolta } from "../utils/formatarPrevisaoDeVolta";
import { segundosRestantesAte } from "../utils/gracePeriodPhase";

/**
 * Aviso GRANDE de manutenção nas telas de login (pedido do Augusto,
 * 28/09/2026): "exibir um grande aviso na tela de login informando que está
 * em manutenção e pedindo que as pessoas não façam login".
 *
 * Aparece desde a contagem regressiva (manutenção ativada, carência de até
 * 10 min correndo) e continua depois de travar. NUNCA bloqueia o formulário:
 * o administrador precisa entrar para operar a manutenção — quem barra os
 * demais é o backend (503). Daqui só sai o aviso; quando mostrar e onde
 * encaixar é decisão de cada página (`pages/Login.tsx`,
 * `pages/portal/PortalLogin.tsx`).
 *
 * Desenho: o painel é âmbar nos dois públicos (âmbar = manutenção em todo o
 * produto: MaintenanceBanner, modal de carência); o que muda por público é o
 * selo e o rótulo, que carregam a cor da própria página (teal `ep-*` no
 * SeWeb, esmeralda no Portal do Parceiro) — o aviso é "a casa avisando", não
 * um alerta genérico colado por cima.
 */

export type TipoDoAvisoDeManutencao = "contagem" | "travada";

export interface AvisoDeManutencaoNoLoginProps {
  tipo: TipoDoAvisoDeManutencao;
  /** Texto livre do admin que ativou (backend `message`); pode vir vazio/null. */
  mensagem?: string | null;
  /** ISO — previsão de volta (`estimated_end`); opcional. */
  previsaoDeVolta?: string | null;
  /** ISO — fim da contagem (`grace_period_ends_at`); vem quando tipo="contagem". */
  fimDaContagem?: string | null;
  /** "interno" = /login; "parceiro" = /portal/login (muda a paleta e o texto do admin). */
  publico: "interno" | "parceiro";
}

type Publico = AvisoDeManutencaoNoLoginProps["publico"];

/** O que muda por página: o selo e o rótulo levam a cor da casa. */
const PALETA: Record<Publico, { selo: string; rotulo: string }> = {
  interno: {
    selo: "bg-ep-primary text-ep-on-primary dark:bg-ep-primary-container dark:text-ep-primary",
    rotulo: "text-ep-primary dark:text-ep-primary-container",
  },
  parceiro: {
    selo: "bg-emerald-600 text-white dark:bg-emerald-400 dark:text-emerald-950",
    rotulo: "text-emerald-800 dark:text-emerald-300",
  },
};

const TEXTOS: Record<TipoDoAvisoDeManutencao, { rotulo: string; titulo: string; apoio: string }> = {
  contagem: {
    rotulo: "Manutenção começa em breve",
    titulo: "O sistema vai entrar em manutenção",
    apoio: "Quem entrar será desconectado assim que a manutenção começar.",
  },
  travada: {
    rotulo: "Manutenção em andamento",
    titulo: "Sistema em manutenção",
    apoio: "Volte mais tarde, quando a manutenção terminar.",
  },
};

const PEDIDO = "Não faça login agora.";
const LINHA_DO_ADMIN = "Somente administradores podem entrar durante a manutenção.";

function calcularSegundosRestantes(fimIso: string | null | undefined): number {
  const restante = segundosRestantesAte(fimIso);
  if (restante === null) return 0;
  return Math.max(0, Math.ceil(restante));
}

/**
 * Segundos inteiros até `fimIso`, recalculados a cada segundo a partir do
 * relógio (não acumula deriva; aba em segundo plano volta certa no tique
 * seguinte). Para em zero — nunca fica negativo.
 */
function useSegundosRestantes(fimIso: string | null | undefined): number {
  const [segundos, setSegundos] = useState(() => calcularSegundosRestantes(fimIso));

  useEffect(() => {
    const inicial = calcularSegundosRestantes(fimIso);
    setSegundos(inicial);
    if (inicial <= 0) return undefined;

    const id = setInterval(() => {
      const restante = calcularSegundosRestantes(fimIso);
      setSegundos(restante);
      if (restante <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [fimIso]);

  return segundos;
}

function formatarMmSs(totalSegundos: number): string {
  const s = Math.max(0, totalSegundos);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * Texto do leitor de tela: muda no máximo uma vez por minuto (o relógio
 * visível muda a cada segundo e fica FORA da árvore de acessibilidade —
 * senão a região `aria-live` do aviso anunciaria cada tique).
 */
function textoParaLeitorDeTela(segundos: number): string {
  if (segundos <= 0) return "A manutenção começa a qualquer momento.";
  const minutos = Math.ceil(segundos / 60);
  if (minutos <= 1) return "Falta menos de um minuto para a manutenção começar.";
  return `Faltam cerca de ${minutos} minutos para a manutenção começar.`;
}

function RelogioDaContagem({ fimIso }: { fimIso: string | null | undefined }): JSX.Element {
  const segundos = useSegundosRestantes(fimIso);
  const zerou = segundos <= 0;

  return (
    <div className="mt-4">
      <p
        aria-hidden="true"
        className="font-label text-[11px] font-medium uppercase tracking-widest text-amber-800/80 dark:text-amber-200/70"
      >
        {zerou ? "Começa" : "Começa em"}
      </p>
      <p
        data-testid="relogio-da-contagem"
        aria-hidden="true"
        aria-live="off"
        className={cn(
          "mt-1 font-headline font-extrabold tracking-tight text-amber-950 dark:text-amber-50",
          zerou ? "text-2xl sm:text-3xl" : "text-5xl leading-none tabular-nums sm:text-6xl"
        )}
      >
        {zerou ? "a qualquer momento" : formatarMmSs(segundos)}
      </p>
      <p className="sr-only" aria-atomic="true">
        {textoParaLeitorDeTela(segundos)}
      </p>
    </div>
  );
}

function normalizar(texto: string): string {
  return texto
    .trim()
    .replace(/[.!]+$/, "")
    .toLowerCase();
}

export function AvisoDeManutencaoNoLogin(props: AvisoDeManutencaoNoLoginProps): JSX.Element {
  const { tipo, mensagem, previsaoDeVolta, fimDaContagem, publico } = props;
  const paleta = PALETA[publico];
  const textos = TEXTOS[tipo];
  const previsao = formatarPrevisaoDeVolta(previsaoDeVolta);

  // A mensagem só entra se disser algo além do título (o Login manda
  // "Sistema em manutenção." como fallback — repetir o título é ruído).
  const mensagemLimpa = mensagem?.trim() || null;
  const mensagemVisivel =
    mensagemLimpa && normalizar(mensagemLimpa) !== normalizar(textos.titulo) ? mensagemLimpa : null;

  return (
    <div
      data-testid="aviso-manutencao"
      data-tipo={tipo}
      role="status"
      aria-live="polite"
      className={cn(
        "mb-6 rounded-xl border border-amber-300 bg-gradient-to-b from-amber-100 to-amber-50 px-5 py-5 text-center text-amber-900",
        "shadow-[0_8px_24px_rgba(217,119,6,0.14)]",
        "dark:border-amber-700/60 dark:from-amber-950/70 dark:to-amber-950/40 dark:text-amber-100 dark:shadow-[0_8px_24px_rgba(0,0,0,0.35)]",
        "motion-safe:animate-ep-fade-in"
      )}
    >
      {/* Selo + rótulo: a cor da casa (teal no SeWeb, esmeralda no Portal) */}
      <div className="flex items-center justify-center gap-2">
        <span
          aria-hidden="true"
          className={cn(
            "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            paleta.selo
          )}
        >
          <Wrench className="h-4 w-4 motion-safe:animate-[spin_8s_ease-in-out_infinite]" />
        </span>
        <p
          className={cn(
            "font-label text-[11px] font-semibold uppercase tracking-widest",
            paleta.rotulo
          )}
        >
          {textos.rotulo}
        </p>
      </div>

      <h2 className="mt-2 font-headline text-2xl font-extrabold leading-tight tracking-tight text-amber-950 sm:text-3xl dark:text-amber-50">
        {textos.titulo}
      </h2>

      {tipo === "contagem" && <RelogioDaContagem fimIso={fimDaContagem} />}

      <p className="mt-4 font-headline text-lg font-bold text-amber-950 dark:text-amber-50">
        {PEDIDO}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-amber-900/90 dark:text-amber-100/90">
        {textos.apoio}
      </p>

      {mensagemVisivel && (
        <p className="mt-4 line-clamp-3 rounded-lg bg-white/70 px-3 py-2 text-sm leading-relaxed text-amber-950 sm:line-clamp-none dark:bg-black/25 dark:text-amber-50">
          {mensagemVisivel}
        </p>
      )}

      {previsao && (
        <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-amber-900 dark:text-amber-100">
          <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Previsão de volta:</span>
          <span className="font-semibold tabular-nums">{previsao}</span>
        </p>
      )}

      {publico === "interno" && (
        <p className="mt-3 text-xs text-amber-800/80 dark:text-amber-200/70">{LINHA_DO_ADMIN}</p>
      )}
    </div>
  );
}
